// /signup (spec "Public launch"): an account with email and password, confirmed with the
// 6-digit code we email, or Google / GitHub in one click. Then Plans (/plans), where it's paid
// for, and Getting ready. ?code=FRIEND comes along to Plans; ?tier=&interval= from the
// landing page's pricing too.

import { useEffect, useRef, useState, type FormEvent } from "react";
import { authMethods, inviteInfo, needsPlan, rememberCode, resendCode, signup, verifySignup, type InviteInfo } from "../api";
import { Link, Redirect, navigate, useLocation } from "../router";
import { refreshMe, useSession } from "../session";
import { store } from "../store";
import { PageSplash } from "./Shell";
import { HxPage, HxWindow, WelcomeArt } from "./hx";
import { PasswordInput } from "../ui/PasswordInput";

/** Where a new account goes next: Plans (with the size picked on the landing page), or Getting ready. */
function nextAfterSignup(search: URLSearchParams) {
  if (!needsPlan(store.get().me)) return "/start";
  const q = new URLSearchParams();
  for (const k of ["tier", "interval"]) {
    const v = search.get(k);
    if (v) q.set(k, v);
  }
  return "/plans" + (q.size ? `?${q}` : "");
}

/** The Google / GitHub buttons this hub offers. next: where they land signed in. */
export function OAuthButtons({ next, verb = "Continue" }: { next: string; verb?: string }) {
  const [methods, setMethods] = useState<("google" | "github")[]>([]);
  useEffect(() => {
    void authMethods().then((r) => r.ok && setMethods(r.data.oauth));
  }, []);
  if (!methods.length) return null;
  return (
    <div className="hx-oauth" data-testid="oauth">
      {methods.map((m) => (
        <a key={m} className="hx-btn hx-btn-secondary hx-btn-block" href={`/auth/${m}?next=${encodeURIComponent(next)}`} data-testid={`oauth-${m}`}>
          {m === "google" ? GoogleMark : GitHubMark}
          {verb} with {m === "google" ? "Google" : "GitHub"}
        </a>
      ))}
      <div className="hx-divider">or</div>
    </div>
  );
}

const GoogleMark = (
  <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true">
    <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.71v2.26h2.92a8.78 8.78 0 0 0 2.68-6.61z" />
    <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
    <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z" />
    <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
  </svg>
);

const GitHubMark = (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" fill="currentColor">
    <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z" />
  </svg>
);

const RESEND_WAIT = 30; // seconds between codes

/**
 * The emailed code: six digits, typed or pasted (or filled by the phone's keyboard), checked
 * as soon as the sixth lands. onDone: confirmed and signed in.
 */
export function CodeStep({ email, devCode, onBack, onDone }: { email: string; devCode?: string; onBack: () => void; onDone: () => void }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wrong, setWrong] = useState(0); // bumps to shake the boxes again
  const [wait, setWait] = useState(RESEND_WAIT);
  const [sent, setSent] = useState<string | null>(null);
  const [hint, setHint] = useState(devCode);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (wait <= 0) return;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const check = async (c: string) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    const r = await verifySignup(email, c);
    if (r.ok) {
      await refreshMe();
      onDone();
      return;
    }
    setBusy(false);
    setCode("");
    setWrong((n) => n + 1);
    if (r.status === 410) {
      setError("That code expired, so we sent a new one. Check your email.");
      const dev = r.body?.devCode;
      if (typeof dev === "string") setHint(dev);
      setWait(RESEND_WAIT);
    } else setError(r.status === 429 ? "Too many tries. Wait a few minutes, then try again." : r.error);
  };
  // Back to the first box once a check is done (the input is disabled while it runs).
  useEffect(() => {
    if (!busy) input.current?.focus();
  }, [busy]);

  const type = (v: string) => {
    const digits = v.replace(/\D/g, "").slice(0, 6);
    setCode(digits);
    setError(null);
    if (digits.length === 6) void check(digits);
  };

  const resend = async () => {
    setWait(RESEND_WAIT);
    setError(null);
    const r = await resendCode(email);
    if (!r.ok) return setError(r.error);
    if (r.data.devCode) setHint(r.data.devCode);
    setSent("A new code is on its way.");
    input.current?.focus();
  };

  return (
    <>
      <div className="hx-column-head hx-rise">
        <WelcomeArt />
        <h1 className="hx-h1">
          Check <em>your email.</em>
        </h1>
        <p>
          We sent a 6-digit code to <b>{email}</b>. It works for 15 minutes.
        </p>
      </div>
      <div className="hx-rise" style={{ ["--i" as string]: 1 }}>
<HxWindow>
<form className="hx-form" onSubmit={(e: FormEvent) => (e.preventDefault(), code.length === 6 && void check(code))} noValidate>
        <div className="hx-field">
          <label htmlFor="signup-code" className="sr-only">
            Code
          </label>
          <div className="hx-otp" data-shake={wrong} data-busy={busy}>
            <input
              id="signup-code"
              ref={input}
              className="hx-otp-input"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              maxLength={6}
              value={code}
              disabled={busy}
              onChange={(e) => type(e.target.value)}
              aria-invalid={!!error}
              aria-describedby={error ? "code-error" : undefined}
              data-testid="signup-code"
            />
            <div className="hx-otp-boxes" aria-hidden="true" key={wrong}>
              {Array.from({ length: 6 }, (_, i) => (
                <span key={i} className="hx-otp-box" data-filled={i < code.length} data-at={i === Math.min(code.length, 5) && !busy}>
                  {code[i] ?? ""}
                </span>
              ))}
            </div>
          </div>
        </div>
        {error && (
          <div className="hx-error" id="code-error" role="alert" data-testid="code-error">
            {error}
          </div>
        )}
        {sent && !error && (
          <p className="hx-note" role="status">
            {sent}
          </p>
        )}
        {hint && (
          <p className="hx-field-hint" data-testid="dev-code">
            Dev hub, no email: the code is {hint}.
          </p>
        )}
        {/* no button: it checks as soon as the sixth digit lands, as the Mac moves on by itself */}
        {busy && (
          <p className="hx-note" role="status" data-testid="code-checking">
            Checking your code…
          </p>
        )}
      </form>
</HxWindow>
</div>
      <p className="hx-foot hx-rise" style={{ ["--i" as string]: 2 }}>
        {wait > 0 ? (
          <span className="hx-faint">New code in {wait}s</span>
        ) : (
          <button type="button" className="hx-linkbtn" onClick={() => void resend()} data-testid="code-resend">
            Send a new code
          </button>
        )}{" "}
        ·{" "}
        <button type="button" className="hx-linkbtn" onClick={onBack} data-testid="code-back">
          Use another email
        </button>
      </p>
    </>
  );
}

/** Sign-up; with invite (an internal tester's link, spec "Internal testers"), the account is
 * theirs: free, no Plans. */
export function SignupPage({ invite }: { invite?: string } = {}) {
  const { auth } = useSession();
  const [inv, setInv] = useState<InviteInfo | null>(null);
  useEffect(() => {
    if (invite) void inviteInfo(invite).then((r) => setInv(r.ok ? r.data : { valid: false, status: "invalid", error: r.error }));
  }, [invite]);
  const { search } = useLocation();
  const [email, setEmail] = useState(search.get("email") ?? "");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<{ email: string; devCode?: string } | null>(null);
  const [, setBillingOn] = useState(true);
  const [open, setOpen] = useState(true);

  const code = search.get("code");
  useEffect(() => rememberCode(code), [code]);
  useEffect(() => {
    void authMethods().then((r) => r.ok && (setBillingOn(r.data.billing), setOpen(r.data.signup)));
  }, []);

  if (auth.kind === "unknown" || (invite && !inv)) return <PageSplash />;
  if (auth.kind === "in" && !step) return <Redirect to={nextAfterSignup(search)} />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!email.trim() || !password) return setError("Enter your email and a password.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return setError("Enter a valid email address.");
    if (password.length < 10) return setError("Use at least 10 characters for your password.");
    setBusy(true);
    setError(null);
    const r = await signup(email.trim(), password, invite);
    setBusy(false);
    if (r.ok) return setStep({ email: r.data.email, devCode: r.data.devCode });
    setError(
      r.status === 409 && !invite
        ? "There’s already an account with this email. Sign in instead."
        : r.status === 429
          ? "Too many tries. Wait a few minutes, then try again."
          : r.error,
    );
  };

  const plansNext = nextAfterSignup(search).startsWith("/plans") ? nextAfterSignup(search) : "/plans";
  return (
    <HxPage
      testid="signup"
      top={
        <Link to="/login" className="hx-btn hx-btn-quiet hx-btn-sm">
          Sign in
        </Link>
      }
    >
      <div className="hx-column">
        {step ? (
          <CodeStep
            email={step.email}
            devCode={step.devCode}
            onBack={() => (setStep(null), setPassword(""))}
            onDone={() => navigate(nextAfterSignup(search), { replace: true })}
          />
        ) : invite && inv && !inv.valid ? (
          <div className="hx-column-head hx-rise" data-testid="invite-invalid">
            <WelcomeArt />
            <h1 className="hx-h1">
              This invite <em>doesn’t work.</em>
            </h1>
            <p>{inv.error ?? "This invite link isn’t valid."} Already have an account?</p>
            <Link to="/login" className="hx-btn hx-btn-primary">
              Sign in
            </Link>
          </div>
        ) : !open && !invite ? (
          <div className="hx-column-head hx-rise" data-testid="signup-closed">
            <WelcomeArt />
            <h1 className="hx-h1">
              Sign-up opens <em>soon.</em>
            </h1>
            <p>cmx opens to everyone with plans, shortly. Already have an account?</p>
            <Link to="/login" className="hx-btn hx-btn-primary">
              Sign in
            </Link>
          </div>
        ) : (
          <>
            <div className="hx-column-head hx-rise">
              <WelcomeArt />
              {invite ? (
                <>
                  <h1 className="hx-h1" data-testid="invite-title">
                    You’re invited <em>to test cmx.</em>
                  </h1>
                  <p>
                    Make your account: your {inv?.tier ?? "cloud machine"} is on us, and it starts as soon as you confirm your email. No card
                    needed.
                  </p>
                </>
              ) : (
                <>
                  <h1 className="hx-h1">
                    Your computer <em>that never sleeps.</em>
                  </h1>
                  <p>Make your account, pick a size, and your cloud machine is ready in about two minutes.</p>
                </>
              )}
            </div>
            {!invite && (
              <div className="hx-rise" style={{ ["--i" as string]: 1 }}>
                <OAuthButtons next={plansNext} />
              </div>
            )}
            <div className="hx-rise" style={{ ["--i" as string]: 2 }}>
<HxWindow>
<form className="hx-form" onSubmit={submit} noValidate>
              <div className="hx-field">
                <label htmlFor="signup-email">Email</label>
                <input
                  id="signup-email"
                  type="email"
                  autoComplete="username"
                  inputMode="email"
                  autoFocus
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-invalid={!!error}
                  data-testid="signup-email"
                />
              </div>
              <div className="hx-field">
                <label htmlFor="signup-password">Password</label>
                <PasswordInput
                  id="signup-password"
                  autoComplete="new-password"
                  placeholder="Create a password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  aria-invalid={!!error}
                  data-testid="signup-password"
                />
                <span className="hx-field-hint">At least 10 characters.</span>
              </div>
              {error && (
                <div className="hx-error" role="alert" data-testid="signup-error">
                  {error}
                </div>
              )}
              <button className="hx-btn hx-btn-primary hx-btn-block" type="submit" disabled={busy} data-testid="signup-submit">
                {busy ? "Creating your account…" : "Create account"}
              </button>
            </form>
</HxWindow>
</div>
            <p className="hx-legal hx-rise" style={{ ["--i" as string]: 3 }}>
              By continuing you agree to the <Link to="/terms">Terms</Link>, <Link to="/privacy">Privacy</Link> and{" "}
              <Link to="/acceptable-use">Acceptable use</Link>.
            </p>
            <p className="hx-foot hx-rise" style={{ ["--i" as string]: 4 }}>
              Already have an account? <Link to="/login">Sign in</Link>
            </p>
          </>
        )}
      </div>
    </HxPage>
  );
}
